import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  DeliveryListTotals,
  DeliveryStatus,
  ScheduledDelivery,
  Supplier,
} from "../api/types";
import { DeliveryCardList } from "../components/deliveries/DeliveryCardList";
import { money } from "../i18n/intl";
import { useAuth } from "../state/AuthContext";
import { deliveryHeadline, deliveryListTotals } from "../utils/deliveryTotals";
// G-94 retired the shop dashboard and moved this type to its own module.
import type { DashboardOutletContext } from "./dashboardOutletContext";

export function DeliveriesPage() {
  const { user } = useAuth();
  const { locations } = useOutletContext<DashboardOutletContext>();
  const isOwner = user?.role === "OWNER";
  const isFlorist = user?.role === "FLORIST";

  const [rows, setRows] = useState<ScheduledDelivery[]>([]);
  // G-169: the strip's figures over the same filters, added once on the server.
  const [listTotals, setListTotals] = useState<DeliveryListTotals | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [filterLocation, setFilterLocation] = useState("");
  const [filterStatus, setFilterStatus] = useState<DeliveryStatus | "">("");
  const [filterSupplier, setFilterSupplier] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // G-137 (OD-184): the home page's "no document" row lands here filtered;
  // which deliveries are missing one is the server's `documentState`.
  const [searchParams, setSearchParams] = useSearchParams();
  const onlyMissingDocument = !isFlorist && searchParams.get("documents") === "missing";

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = {
        locationId: isOwner && filterLocation ? filterLocation : undefined,
        status:
          isFlorist || filterStatus === ""
            ? undefined
            : (filterStatus as DeliveryStatus),
        supplierId: filterSupplier || undefined,
      };
      const [list, totals] = await Promise.all([
        api.listScheduledDeliveries(query),
        api.getScheduledDeliveryTotals({ ...query, missingDocument: onlyMissingDocument }),
      ]);
      setRows(list);
      setListTotals(totals);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load deliveries");
    } finally {
      setLoading(false);
    }
  }, [isOwner, isFlorist, filterLocation, filterStatus, filterSupplier, onlyMissingDocument]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    void api
      .listSuppliers()
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
  }, []);

  const shown = useMemo(
    () => (onlyMissingDocument ? rows.filter((d) => d.documentState === "missing") : rows),
    [rows, onlyMissingDocument],
  );

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div className="page-header">
        <div>
          <h2 className="page-title">Incoming stock</h2>
          <p className="page-subtitle">
            {isFlorist
              ? "Confirm each line as it arrives. Tap + when an item came in exactly as scheduled."
              : "Plan supplier deliveries and track confirmation against the invoice."}
          </p>
        </div>
        {!isFlorist ? (
          <div
            style={{
              display: "flex",
              gap: "var(--space-2)",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Link to="/app/invoices" className="button tinted">
              Supplier papers
            </Link>
            <Link to="/app/invoices/upload" className="button tinted">
              Upload invoice
            </Link>
            <Link to="/app/deliveries/new" className="button">
              Schedule a delivery
            </Link>
          </div>
        ) : null}
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      {onlyMissingDocument ? (
        <div className="alert warning dlv-docs-filter">
          <span>Showing deliveries typed by hand that arrived with no document.</span>
          <button type="button" className="button plain" onClick={() => setSearchParams({})}>
            Show all
          </button>
        </div>
      ) : null}

      <div
        className="card"
        style={{
          display: "flex",
          gap: "var(--space-3)",
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        {isOwner ? (
          <div className="field" style={{ flex: 1, minWidth: 200 }}>
            <label htmlFor="filter-location">Shop</label>
            <select
              id="filter-location"
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
            >
              <option value="">All shops</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="field" style={{ flex: 1, minWidth: 200 }}>
          <label htmlFor="filter-supplier">Supplier</label>
          <select
            id="filter-supplier"
            value={filterSupplier}
            onChange={(e) => setFilterSupplier(e.target.value)}
          >
            <option value="">All suppliers</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!isFlorist ? (
        <div
          className="card"
          style={{ display: "flex", gap: "var(--space-5)", flexWrap: "wrap" }}
        >
          <Stat label="Deliveries" value={String(shown.length)} />
          <Stat
            label="Pending"
            value={String(
              shown.filter((d) => d.status === "SCHEDULED").length,
            )}
          />
          <Stat
            label="In progress"
            value={String(
              shown.filter((d) => d.status === "IN_PROGRESS").length,
            )}
          />
          {/* G-95: the page tracks money and never showed a total of it. The
              value of what is shown belongs beside the counts. G-121 S2: the
              supplier's billed total when every delivery listed has one,
              otherwise the expected value — "(typed)" only when none of the
              listed deliveries came from an invoice. What did not arrive is
              "not received" — information, not a receivable. */}
          {(() => {
            if (!listTotals) return null;
            const figures = deliveryListTotals(listTotals);
            const lead = deliveryHeadline(figures, listTotals.anyInvoice);
            return (
              <>
                <Stat label={lead.label} value={money(lead.amount)} />
                <Stat
                  label="Not received"
                  value={money(figures.shortageEur)}
                  alarm={figures.shortageEur > 0}
                />
              </>
            );
          })()}
        </div>
      ) : null}

      {/*
        * G-95 built these cards for the phone, in place of a table that could
        * not be scrolled past its third column. Owner, 2026-10-03: the cards
        * read better than the table at every width, so the table is gone and
        * the status chips are the one status filter.
        */}
      <DeliveryCardList
        rows={shown}
        status={isFlorist ? null : filterStatus}
        onStatusChange={setFilterStatus}
        confirmLabel={isFlorist}
        loading={loading}
      />
    </section>
  );
}

function Stat({
  label,
  value,
  alarm = false,
}: {
  label: string;
  value: string;
  /** Money that is missing reads as missing, not as one more number. */
  alarm?: boolean;
}) {
  return (
    <div>
      <div
        className="muted"
        style={{ fontSize: 12, textTransform: "uppercase" }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 600,
          color: alarm ? "var(--color-danger)" : undefined,
        }}
      >
        {value}
      </div>
    </div>
  );
}
